/**
 * locationPermission
 * ──────────────────
 * Thin wrapper around the Permissions API + Geolocation API so the GPS
 * gate has a single source of truth across the Tech tab.
 *
 *   • `checkPermission()` — async, returns "granted" | "denied" | "prompt"
 *                           Falls back to "prompt" when the browser
 *                           doesn't expose the Permissions API (some
 *                           older iOS Safari builds).
 *   • `requestPermission()` — fires a single `getCurrentPosition` which
 *                           is the only documented way to prompt the
 *                           user on iOS Safari.
 *   • `onChange(cb)`      — subscribes to live permission changes (the
 *                           user opens Settings → Location and toggles
 *                           — we want to drop the gate immediately).
 */
export async function checkPermission() {
  if (typeof navigator === "undefined" || !navigator.geolocation) return "denied";
  if (!navigator.permissions || !navigator.permissions.query) return "prompt";
  try {
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state; // "granted" | "denied" | "prompt"
  } catch {
    return "prompt";
  }
}

export function requestPermission() {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) return resolve("denied");
    navigator.geolocation.getCurrentPosition(
      () => resolve("granted"),
      (err) => resolve(err && err.code === 1 ? "denied" : "prompt"),
      { timeout: 10000, enableHighAccuracy: false }
    );
  });
}

export function onChange(cb) {
  if (typeof navigator === "undefined" || !navigator.permissions || !navigator.permissions.query) {
    return () => {};
  }
  let cleanup = () => {};
  navigator.permissions.query({ name: "geolocation" }).then((status) => {
    const handler = () => cb(status.state);
    status.addEventListener("change", handler);
    cleanup = () => status.removeEventListener("change", handler);
  }).catch(() => {});
  return () => cleanup();
}
